import type { InspectionStatusId, InspectionType } from '@areapif/areapif-shared'
import { Chip } from '@mui/material'
import { FC } from 'react'
import { useTranslate } from 'react-admin'

import { InspectionState } from '../../domain/activeInspection'
import { formatStateBadgeLabel } from '../../domain/activeInspectionCells'

type ChipColor = 'default' | 'info' | 'success' | 'error'

/**
 * One colour per lifecycle state: grey for not-yet-started, blue while work is
 * happening, green when finished, red when cancelled.
 */
const STATE_COLORS: Record<InspectionState, ChipColor> = {
  [InspectionState.PLANNED]: 'default',
  [InspectionState.IN_PROGRESS]: 'info',
  [InspectionState.FINISHED]: 'success',
  [InspectionState.CANCELLED]: 'error',
}

export interface InspectionStateBadgeProps {
  state: InspectionState
  /** Raw SOSTRAT status, shown as a tooltip so the merged states stay traceable. */
  statusId: InspectionStatusId
  /** Which lifecycle the row follows: the PIF ladder, or the terminal one. */
  type: InspectionType
}

/**
 * The lifecycle state of an inspection, as one chip.
 *
 * The label — the state, qualified with the raw status on a PIF row — is
 * spelled out by `formatStateBadgeLabel`, which the CSV export writes too, so
 * the file names a state exactly as the chip does.
 */
export const InspectionStateBadge: FC<InspectionStateBadgeProps> = ({ state, statusId, type }) => {
  const translate = useTranslate()

  return (
    <Chip
      data-testid="inspection-state-badge"
      size="small"
      color={STATE_COLORS[state]}
      variant={state === InspectionState.PLANNED ? 'outlined' : 'filled'}
      label={formatStateBadgeLabel(translate, { state, statusId, type })}
      title={statusId}
    />
  )
}
